import {
  verifyTokenStarted,
  verifyUserSuccess,
  verifyTokenEnd,
  userLoginStarted,
  userLoginFailure,
  userLogout,
} from "./auth.action";

import {
  verifyTokenService,
  userLoginService,
  userLogoutService,
  userLoginByAdminService,
  userImpersonateService,
  userLoginLogsService,
} from "../services/auth.service";

// handle verify token
export const verifyTokenAsync =
  (silentAuth = false) =>
  async (dispatch) => {
    dispatch(verifyTokenStarted(silentAuth));

    const result = await verifyTokenService();

    if (result?.error) {
      dispatch(verifyTokenEnd());
      if (result.response && [401, 403].includes(result.response.status))
        dispatch(userLogout());
      return;
    }

    if (result?.status === 204) {
      dispatch(verifyTokenEnd());
    } else {
      const { token, expiredAt, user, loginBy } = result?.data || {};
      dispatch(verifyUserSuccess({ token, expiredAt, user, loginBy }));
    }
  };

// handle user login
export const userLoginAsync =
  (
    email,
    password,
    language,
    createdBy,
    isSignUpByGoogle,
    isSignUpByFacebook,
  ) =>
  async (dispatch) => {
    dispatch(userLoginStarted());

    const result = await userLoginService(
      email,
      password,
      language,
      createdBy,
      isSignUpByGoogle,
      isSignUpByFacebook,
    );

    if (result?.error) {
      dispatch(userLoginFailure(result.response));
      return;
    }
    await userLoginLogsService(result.data?.user?.userId);
    dispatch(verifyUserSuccess(result.data));
  };

// handle user logout
export const userLogoutAsync = () => (dispatch) => {
  dispatch(userLogout());
  userLogoutService();
};

/**
 * Staff impersonation. Kept separate from userLoginByAdminAsync below rather
 * than overloading it: that one also serves post-signup self-login, and giving
 * one action two authentication shapes is how the next person ships a bug.
 */
export const userImpersonateAsync = (token) => async (dispatch) => {
  dispatch(userLoginStarted());

  const result = await userImpersonateService(token);
  if (result?.error) {
    dispatch(userLoginFailure(result.response || "Impersonation link rejected."));
    // Fall through to a normal session check rather than returning. App.js
    // returns early when ?token= is present, so without this NOTHING else
    // dispatches and the app spins forever on a failed grant.
    //
    // That is not an edge case: a grant lives two minutes and is single use, so
    // a stale link, a refresh, or a second click all land here. Found by opening
    // a deliberately-invalid grant against dev — the request 401'd correctly and
    // the page then hung, which is the half a unit test would not have caught.
    await dispatch(verifyTokenAsync());
    return;
  }
  await dispatch(verifyUserSuccess(result.data));
};

export const userLoginByAdminAsync =
  (userId, createdBy, isLoginByCustomer, navigate, setDatasheetBtnDisable, sessionGrant) =>
  async (dispatch) => {
    dispatch(userLoginStarted());

    // App.js's legacy ?userId= path passes `true` here, not a function, so this
    // has to be type-checked rather than truth-checked. Only reachable since the
    // failure branch below started re-enabling the button — before that the
    // boolean was never called and the mismatch was invisible.
    const reenableDatasheetBtn = () => {
      if (typeof setDatasheetBtnDisable === "function") {
        setDatasheetBtnDisable(false);
      }
    };

    // Prefer the signed grant the signup response now returns. The identifiers
    // then come out of the SIGNATURE rather than out of client state, which is
    // what stops this endpoint accepting any customer's ObjectId from anyone.
    // Falls back to the old shape when no grant is present, so an older server
    // — or a flow that does not mint one — behaves exactly as before.
    const result = sessionGrant
      ? await userImpersonateService(sessionGrant)
      : await userLoginByAdminService(userId, createdBy);
    if (result?.error) {
      dispatch(userLoginFailure(result.response));
      // Re-enable the button. It was only ever re-enabled on success, so any
      // failure left "Complete Data Sheet" disabled forever with no way back
      // but a reload — a pre-existing bug that a grant makes reachable, since
      // an expired grant is a normal outcome where a wrong ObjectId was not.
      reenableDatasheetBtn();
      return;
    }
    await dispatch(verifyUserSuccess(result.data));

    if (isLoginByCustomer) {
      await userLoginLogsService(userId);
      reenableDatasheetBtn();
      navigate(`/datasheets/data?id=${userId}`);
    }
  };
